import React, {useContext, useEffect, useState, useMemo} from "react";
import {Space, Button, Input, Select, Form, Typography, message, Card, Table, Tag as AntTag, Tooltip, Segmented} from 'antd';
import {connect, useDispatch} from "react-redux";
import {testModel, getModels, getTags} from "../../util/redux/asyncActions";
import {clearTestResult} from "../../util/redux/index";
import ManyosSpinner from "../../ManyosSpinner/ManyosSpinner";
import {AuthContext} from "../../Auth/AuthProvider";
import Tag from "../../Tag/Tag";
import AnalyzedText from "../../AnalyzedText/AnalyzedText";
import {RECOGNIZER_LABELS} from "../../util/constants";
import {PlayCircleOutlined} from "@ant-design/icons";

const {Text} = Typography;
const {TextArea} = Input;

const select = state => {
    const files = state.request.files;
    const loading = state.request.loading;
    const testResult = state.request.testResult;
    const testDurationMs = state.request.testDurationMs;
    const analysis = state.request.analysis;
    const tags = state.request.all_entities;
    const allTags = state.request.tags;
    const models = state.request.models;
    const dynamicTagging = state.request.dynamicTagging;
    const privacyFilter = state.request.privacyFilter;
    const entities = state.request.entities;
    const config = state.request.config;
    return {
        files, models, testResult, testDurationMs, loading, tags, analysis, allTags,
        dynamicTagging, privacyFilter, entities, config
    };
};

function TestModel(props) {

    const {files, loading, testResult, testDurationMs, models, tags, analysis, allTags, dynamicTagging, privacyFilter, entities, config} = props;
    const dispatch = useDispatch();
    const userManager = useContext(AuthContext);

    useEffect(() => {
        dispatch(getModels({userManager}));
    }, []);

    const [testString, setTestString] = useState("");
    const [testMode, setTestMode] = useState("config");
    const [model, setModel] = useState();
    const [removeTags, setRemoveTags] = useState([]);

    const llmTagging = useMemo(() => {
        try {
            const parsed = JSON.parse(config);
            return parsed?.llmTagging || null;
        } catch (e) {
            return null;
        }
    }, [config]);

    const extendedModels = useMemo(() => {
        const modelList = models ? [...models] : [];
        if (llmTagging && llmTagging.url && llmTagging.model) {
            modelList.unshift({
                model: "__llm_tagging__",
                type: "llm",
                lang: "multi",
                displayName: "LLM Tagging (Gateway)"
            });
        }
        if (privacyFilter && privacyFilter.model) {
            modelList.unshift({
                model: "__privacy_filter__",
                type: "privacy-filter",
                lang: "multi",
                displayName: "Privacy Filter"
            });
        }
        if (dynamicTagging && dynamicTagging.model) {
            modelList.unshift({
                model: "__dynamic_tagging__",
                type: "gliner",
                lang: "multi",
                displayName: "Dynamic Tagging"
            });
        }
        return modelList;
    }, [models, dynamicTagging, privacyFilter, llmTagging]);

    const selectedModelDef = useMemo(() => {
        if (!model) return null;
        return extendedModels.find(m => m.model === model) || null;
    }, [model, extendedModels]);

    React.useEffect(() => {
        if (tags && tags.length > 0) {
            setRemoveTags(tags);
        }
    }, [tags]);

    React.useEffect(() => {
        if (testMode === "config") {
            const entityKeys = entities ? Object.keys(entities) : [];
            setRemoveTags(entityKeys);
            setModel(undefined);
        }
    }, [testMode]);

    // entity-driven recognizers (Dynamic Tagging / LLM): load ALL configured
    // entities when the model is selected - the user can then deselect
    React.useEffect(() => {
        if (selectedModelDef?.type === "gliner" || selectedModelDef?.type === "llm") {
            setRemoveTags(entities ? Object.keys(entities) : []);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedModelDef?.type]);

    const highlightedOriginal = useMemo(() => {
        const text = testString || "";
        if (!text || analysis.length === 0) {
            return [<span key="orig">{text}</span>];
        }

        const spans = [...analysis].sort((a, b) => {
            if (a.start !== b.start) return a.start - b.start;
            const lenA = (a.end - a.start), lenB = (b.end - b.start);
            if (lenA !== lenB) return lenB - lenA;
            return (b.score || 0) - (a.score || 0);
        });

        const selected = [];
        let lastEnd = -1;
        for (const s of spans) {
            if (s.start >= lastEnd) {
                selected.push(s);
                lastEnd = s.end;
            }
        }

        const nodes = [];
        let cursor = 0;
        selected.forEach((ent, idx) => {
            if (ent.start > cursor) {
                nodes.push(<span key={`txt-${idx}-${cursor}`}>{text.slice(cursor, ent.start)}</span>);
            }
            const value = text.slice(ent.start, ent.end);
            const tagEntry = allTags.find(e => e.tag === ent.type);
            const color = tagEntry ? tagEntry.color : "#ef7721";
            nodes.push(
                <Tooltip
                    key={`tag-${idx}-${ent.start}-${ent.end}`}
                    title={`${ent.score ? (ent.score * 100).toFixed(1) + "%" : ""} ${RECOGNIZER_LABELS[ent.recognizer] || ""}`}
                >
                    <span style={{display: "inline"}}>
                        <Tag
                            tagName={ent.type}
                            mytype="TAG"
                            color={color}
                        >
                            {value}
                        </Tag>
                    </span>
                </Tooltip>
            );
            cursor = ent.end;
        });
        if (cursor < text.length) {
            nodes.push(<span key={`txt-tail-${cursor}`}>{text.slice(cursor)}</span>);
        }
        return nodes;
    }, [testString, analysis, allTags]);

    const anonymizedHighlighted = useMemo(() => {
        if (!testResult) return null;
        return testResult.split(/(<[A-Z_]+>)/g).map((part, i) => {
            if (part.match(/^<[A-Z_]+>$/)) {
                const entityType = part.slice(1, -1);
                const tagEntry = allTags?.find(t => t.tag === entityType);
                const color = tagEntry?.color || "#ef7721";
                return (
                    <Tag key={i} tagName={entityType} color={color} closable={false}/>
                );
            }
            return <span key={i}>{part}</span>;
        });
    }, [testResult, allTags]);

    const handleTestStringChange = (e) => {
        setTestString(e.target.value);
        // stale results don't match the edited text - clear them
        if (testResult || (analysis && analysis.length > 0)) {
            dispatch(clearTestResult());
        }
    };

    const handleModelSelect = (e) => {
        const selModel = extendedModels.find(m => m.model === e);
        if (!selModel) return;

        if (selModel.type === "config") {
            const entityKeys = entities ? Object.keys(entities) : [];
            setRemoveTags(entityKeys);
        } else if (selModel.type === "gliner") {
            const entityKeys = entities ? Object.keys(entities).filter(k => entities[k]?.labels?.length > 0) : [];
            setRemoveTags(entityKeys);
        } else if (selModel.type === "privacy-filter") {
            setRemoveTags(privacyFilter?.entities ? Object.keys(privacyFilter.entities) : []);
        } else {
            const data = {
                "model": {
                    "configuration": {
                        "nlp_engine_name": "spacy",
                        "models": [
                            {
                                "lang_code": selModel.lang,
                                "model_name": (selModel.type === "custom") ? `./models/${selModel.model}` : selModel.model
                            }
                        ]
                    }
                }
            };
            dispatch(getTags({data, userManager}));
        }
        setModel(e);
    };

    const handleRemoveTags = (e) => {
        setRemoveTags(e);
    };

    const tagRender = (props) => {
        const {label, value, closable, onClose} = props;
        const onPreventMouseDown = event => {
            event.preventDefault();
            event.stopPropagation();
        };
        let tagColor;
        if (allTags && Array.isArray(allTags)) {
            const tagEntry = allTags.find(e => e.tag === value);
            if (tagEntry) {
                tagColor = tagEntry.color;
            }
        }

        return (
            <Tag
                color={tagColor}
                onMouseDown={onPreventMouseDown}
                closable={false}
                onClose={onClose}
                style={{marginInlineEnd: 4}}
            >
                {label}
            </Tag>
        );
    };

    const handleRunTest = () => {
        if (!testString || testString.length === 0) {
            message.error("Enter a test string");
            return;
        }
        if (testMode === "config") {
            dispatch(testModel({
                testString,
                modeldef: {type: "config"},
                removeTags,
                dynamicTagging,
                entities,
                userManager,
                savedConfig: config
            }));
        } else if (model) {
            const modeldef = extendedModels.find(m => m.model === model);
            // entity-driven recognizers: the Entities selection restricts what
            // is searched for - send only the selected entity types
            const selectedEntities = Object.fromEntries(
                Object.entries(entities || {}).filter(([k]) => (removeTags || []).includes(k))
            );
            if (modeldef.type === "gliner") {
                dispatch(testModel({
                    testString,
                    modeldef,
                    removeTags: [],
                    dynamicTagging,
                    entities: selectedEntities,
                    userManager
                }));
            } else if (modeldef.type === "privacy-filter") {
                dispatch(testModel({
                    testString,
                    modeldef,
                    removeTags: [],
                    privacyFilter,
                    userManager
                }));
            } else if (modeldef.type === "llm") {
                dispatch(testModel({
                    testString,
                    modeldef,
                    removeTags: [],
                    llmTagging,
                    entities: selectedEntities,
                    userManager
                }));
            } else {
                dispatch(testModel({
                    testString,
                    modeldef,
                    removeTags,
                    dynamicTagging: null,
                    entities: null,
                    userManager
                }));
            }
        } else {
            message.error("Select a model");
        }
    };

    const availableTagOptions = useMemo(() => {
        if (testMode === "config") {
            return entities
                ? Object.keys(entities).map((tag, i) => ({key: i, label: tag, value: tag}))
                : [];
        }
        if (selectedModelDef?.type === "gliner" || selectedModelDef?.type === "llm") {
            return entities
                ? Object.keys(entities).map((tag, i) => ({key: i, label: tag, value: tag}))
                : [];
        }
        return tags ? tags.map((tag, i) => ({key: i, label: tag, value: tag})) : [];
    }, [selectedModelDef, tags, entities]);

    const detectionColumns = [
        {title: "Entity", dataIndex: "type", key: "type", width: 120, render: (t) => <Text strong>{t}</Text>},
        {
            title: "Value", key: "value", render: (_, r) => (
                <Text code>{testString.slice(r.start, r.end)}</Text>
            )
        },
        {
            title: "Source", dataIndex: "recognizer", key: "recognizer", width: 150,
            render: (r) => (
                <AntTag color={r === "gliner" ? "blue" : r === "privacy-filter" ? "purple" : r === "llm" ? "orange" : r === "pattern" ? "cyan" : "green"} style={{fontWeight: 500}}>
                    {RECOGNIZER_LABELS[r] || r || "\u2014"}
                </AntTag>
            )
        },
        {
            title: "Confidence", dataIndex: "score", key: "score", width: 100,
            render: (s) => s ? <Text type={s >= 0.8 ? "success" : s >= 0.5 ? "warning" : "danger"}>{(s * 100).toFixed(1)}%</Text> : "\u2014"
        },
        {title: "Model", dataIndex: "model", key: "model", width: 180, render: (m) => <Text type="secondary">{m}</Text>}
    ];

    return (
        <ManyosSpinner spinning={loading}>
            <div style={{maxWidth: 900, margin: "0 auto", padding: "24px 32px"}}>

                {(!config || config === "{}") && (
                    <div style={{
                        padding: "12px 16px",
                        background: "#fffbe6",
                        border: "1px solid #ffe58f",
                        borderRadius: 8,
                        color: "#8c6d1f",
                        marginBottom: 20,
                        fontSize: 13,
                    }}>
                        No configuration found. Please set up your models and entities in the Configuration tab first.
                    </div>
                )}

                <Form layout="vertical">
                    <Form.Item label="Test Mode">
                        <Segmented
                            value={testMode}
                            onChange={setTestMode}
                            options={[
                                {label: "Saved Config", value: "config"},
                                {label: "Specific Model", value: "model"},
                            ]}
                        />
                    </Form.Item>

                    <div style={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20}}>
                        {testMode === "model" && (
                            <Form.Item label="Model">
                                <Select
                                    onSelect={handleModelSelect}
                                    value={model}
                                    placeholder="Select a model..."
                                    options={extendedModels.map((m, i) => ({
                                        key: i,
                                        label: m.displayName || m.model,
                                        value: m.model
                                    }))}
                                />
                            </Form.Item>
                        )}

                        {availableTagOptions.length > 0 && (
                            <Form.Item label={testMode === "config" || selectedModelDef?.type === "gliner" || selectedModelDef?.type === "llm" ? "Entities" : "Remove Tags"}>
                                <Select
                                    mode="multiple"
                                    allowClear
                                    tagRender={tagRender}
                                    value={removeTags}
                                    onChange={handleRemoveTags}
                                    options={availableTagOptions}
                                />
                            </Form.Item>
                        )}
                    </div>

                    <Form.Item label="Test Text">
                        <TextArea
                            value={testString}
                            onChange={handleTestStringChange}
                            aria-label="Enter text to test"
                            rows={3}
                            placeholder="Enter text to analyze..."
                        />
                    </Form.Item>

                    <Form.Item>
                        <Button
                            onClick={handleRunTest}
                            type="primary"
                            size="large"
                            icon={<PlayCircleOutlined/>}
                            aria-label="Run model test"
                        >
                            Run Test
                        </Button>
                    </Form.Item>
                </Form>

                {testResult && (
                    <>
                        {testDurationMs !== undefined && (
                            <div style={{marginBottom: 8, textAlign: "right"}}>
                                <Text type="secondary" style={{fontSize: 12}}>
                                    Runtime: {(testDurationMs / 1000).toFixed(2)}s
                                </Text>
                            </div>
                        )}
                        <div
                            className="test-results-grid"
                            style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: 20,
                                marginBottom: 20,
                            }}
                        >
                            <Card title="Detected Entities" size="small">
                                <div style={{lineHeight: 2.0, whiteSpace: "pre-wrap"}}>
                                    <AnalyzedText
                                        analysis={analysis}
                                        text={testString}
                                        allTags={allTags}
                                    />
                                </div>
                            </Card>
                            <Card title="Anonymized Text" size="small">
                                <div style={{lineHeight: 2.0, whiteSpace: "pre-wrap"}}>
                                    {anonymizedHighlighted}
                                </div>
                            </Card>
                        </div>

                        {analysis.length > 0 && (
                            <Card title="Detection Details" size="small">
                                <Table
                                    size="small"
                                    pagination={false}
                                    dataSource={analysis.map((a, i) => ({key: i, ...a}))}
                                    columns={detectionColumns}
                                />
                            </Card>
                        )}
                    </>
                )}
            </div>
        </ManyosSpinner>
    );
}

export default connect(select)(TestModel);
